import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Package, RotateCcw } from 'lucide-react';
import { useLang } from '../LanguageProvider';
import { urunMetinleri } from './urunMetinleri';

/**
 * RADAR ANİMASYONU — radar-whowsell/components/landing/hero-preview.tsx PORTU
 *
 * KAYNAK DOSYAYA GÖRE FARKLAR VE HER BİRİNİN GEREKÇESİ:
 *
 * 1) DETAY MODALI TAŞINMADI. Kaynakta satıra tıklayınca `createPortal` ile
 *    `document.body`'ye bir modal basılıyor ve PC'de `document.body.overflow`
 *    kilitleniyordu. Ana sitenin kendi modal/scroll mantığıyla çakışır.
 *    Bu yüzden yalnızca CANLI ARAMA PANELİ taşındı; satırlar artık tıklanabilir
 *    değil (buton yerine `div`), duraklatmanın tek tetikleyicisi fare/dokunma.
 *
 * 2) UNSPLASH GÖRSELLERİ TAŞINDI. İlk portta 12 harici görsel bilerek
 *    kaldırılmıştı; Selçuk "görselleriyle beraber, birebir oradakini" dediği
 *    için hepsi kaynaktaki adresleriyle geri kondu. Kaynağın kendi hata yolu
 *    (`Package` ikonu + marka gradyanı) yedek olarak duruyor — görsel
 *    yüklenemezse kutu boş kalmıyor. Ayrıca `loading="lazy"`,
 *    `decoding="async"` ve `referrerPolicy="no-referrer"` eklendi.
 *
 * 3) TAILWIND 4 -> TAILWIND 3. `bg-brand-gradient` ve `text-brand-gradient`
 *    kaynakta `@utility` ile tanımlıydı; Tailwind 3'te `@utility` yoktur.
 *    İkisi de `App.css` içinde düz sınıfa çevrildi (`.wh-radar-brand-gradient`,
 *    `.wh-radar-brand-text`) ve marka renkleri birebir yazıldı:
 *    #7c3aed -> #d946ef -> #ec4899 (135deg), vurgu #7c3aed, canlı nokta #10b981.
 *    `color-mix(... oklch ...)` yerine düz hex kullanıldı — aynı üç durak.
 *
 * 4) `wh-row-in` / `wh-paused` kuralları `globals.css`'ten `App.css`'e
 *    `.wh-radar-` önekiyle DEĞERLERİ AYNEN taşındı:
 *    0.42s cubic-bezier(0.22, 1, 0.36, 1) both, gecikme calc(var(--i) * 95ms),
 *    from { opacity: 0; transform: translateY(9px) }.
 *
 * 5) `hidden sm:block` (sparkline) ve `hidden sm:inline` (US rozeti) KALDIRILDI.
 *    GEREKÇE: Bu panel `OlcekliKutu` içinde SABİT 420px tasarım genişliğinde
 *    çizilip `transform: scale()` ile küçültülür. Tailwind'in `sm:` kapısı
 *    kutunun değil EKRANIN genişliğine bakar; 280px'lik bir ekranda kutu hâlâ
 *    420px olmasına rağmen sparkline ve US rozeti kaybolurdu. Sürekli görünür
 *    yapıldı — böylece her genişlikte kaynağın masaüstü görüntüsü korunur.
 *
 * Daktilo zamanlamaları (90+rnd*85 ms yazma, 45+rnd*35 ms silme, 4200 ms
 * sonuç bekleme, 750 ms kelime arası, 700 ms açılış, 160 ms devam) kaynakla
 * birebir aynıdır.
 */

const UP = [6, 7, 6, 8, 8, 10, 11, 13];
const FLAT = [9, 8, 9, 7, 8, 7, 6, 7];
const DOWN = [10, 9, 8, 6, 5, 4, 4, 3];

// Verdict dağılımı her aramada FARKLI (sabit Go/Caution/Avoid sırası yok).
const SEARCHES = [
  {
    query: 'wireless headphones',
    count: 128,
    products: [
      { id: 'h1', img: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=120&h=120&q=80', brand: 'AudioX', price: '$89.99', bsr: '#1.234', verdict: 'Go', tone: 'go', spark: UP },
      { id: 'h2', img: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?auto=format&fit=crop&w=120&h=120&q=80', brand: 'SonicWave', price: '$59.90', bsr: '#3.410', verdict: 'Caution', tone: 'caution', spark: FLAT },
      { id: 'h3', img: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?auto=format&fit=crop&w=120&h=120&q=80', brand: 'RunBeat', price: '$24.99', bsr: '#8.920', verdict: 'Avoid', tone: 'avoid', spark: DOWN },
    ],
  },
  {
    query: 'yoga mat',
    count: 96,
    products: [
      { id: 'y1', img: 'https://images.unsplash.com/photo-1592432678016-e910b452f9a2?auto=format&fit=crop&w=120&h=120&q=80', brand: 'FlexFit', price: '$32.99', bsr: '#2.180', verdict: 'Go', tone: 'go', spark: UP },
      { id: 'y2', img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=120&h=120&q=80', brand: 'NomadFit', price: '$18.90', bsr: '#21.300', verdict: 'Avoid', tone: 'avoid', spark: DOWN },
      { id: 'y3', img: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?auto=format&fit=crop&w=120&h=120&q=80', brand: 'PureBalance', price: '$45.00', bsr: '#6.750', verdict: 'Caution', tone: 'caution', spark: FLAT },
    ],
  },
  {
    query: 'iphone case',
    count: 210,
    products: [
      { id: 'c1', img: 'https://images.unsplash.com/photo-1556656793-08538906a9f8?auto=format&fit=crop&w=120&h=120&q=80', brand: 'Maison', price: '$39.99', bsr: '#5.120', verdict: 'Caution', tone: 'caution', spark: FLAT },
      { id: 'c2', img: 'https://images.unsplash.com/photo-1601593346740-925612772716?auto=format&fit=crop&w=120&h=120&q=80', brand: 'ClearGuard', price: '$14.99', bsr: '#940', verdict: 'Go', tone: 'go', spark: UP },
      { id: 'c3', img: 'https://images.unsplash.com/photo-1541877944-ac82a091518a?auto=format&fit=crop&w=120&h=120&q=80', brand: 'CaseHub', price: '$6.50', bsr: '#45.120', verdict: 'Avoid', tone: 'avoid', spark: DOWN },
    ],
  },
  {
    query: 'led strip lights',
    count: 154,
    products: [
      { id: 'l1', img: 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=120&h=120&q=80', brand: 'GlowLine', price: '$21.99', bsr: '#1.870', verdict: 'Go', tone: 'go', spark: UP },
      { id: 'l2', img: 'https://images.unsplash.com/photo-1550985616-10810253b84d?auto=format&fit=crop&w=120&h=120&q=80', brand: 'BiasGlow', price: '$12.99', bsr: '#2.540', verdict: 'Go', tone: 'go', spark: UP },
      { id: 'l3', img: 'https://images.unsplash.com/photo-1517490232338-06b912a786b5?auto=format&fit=crop&w=120&h=120&q=80', brand: 'LumaHome', price: '$34.99', bsr: '#4.980', verdict: 'Caution', tone: 'caution', spark: FLAT },
    ],
  },
];

const TONES = {
  go: { chip: 'border-emerald-500/30 bg-emerald-500/[0.12] text-emerald-500', dot: 'bg-emerald-500', stroke: '#10b981' },
  caution: { chip: 'border-amber-500/30 bg-amber-500/[0.12] text-amber-500', dot: 'bg-amber-500', stroke: '#f59e0b' },
  avoid: { chip: 'border-red-500/30 bg-red-500/[0.12] text-red-500', dot: 'bg-red-500', stroke: '#ef4444' },
};

/**
 * ÜRÜN GÖRSELİ — kaynaktaki `ProductThumb` ile birebir aynı davranış.
 *
 * Selçuk'un açık isteği: "radarın landing page'indekini birebir al, GÖRSELLERİYLE
 * BERABER". Bu yüzden daha önce kaldırılan 12 `images.unsplash.com` adresi geri
 * kondu ve gerçek `<img>` çiziliyor.
 *
 * Kaynaktaki hata yolu aynen korundu: görsel yüklenemezse (ağ yok, adres ölmüş,
 * içerik engelleyici) `Package` ikonu + marka gradyanı zemin devreye giriyor.
 * Yani kutu HİÇBİR durumda boş kalmıyor, düzen kaymıyor — kutu boyutu, kenarlık
 * ve köşe yarıçapı iki durumda da `className`den geliyor.
 *
 * `loading="lazy"` ve `decoding="async"`: panel sayfanın ortasında, ilk boyada
 * 12 harici görsel indirmenin anlamı yok. `referrerPolicy="no-referrer"` ise
 * ziyaretçinin hangi sayfadan geldiğini üçüncü tarafa göndermemek için.
 */
function ProductThumb({ src, alt, className }) {
  const [hata, setHata] = useState(false);
  if (hata || !src) {
    return (
      <div className={`${className} wh-radar-thumb flex items-center justify-center`}>
        <Package className="h-1/2 w-1/2 wh-radar-thumb-icon" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setHata(true)}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className={`${className} object-cover`}
    />
  );
}

function Sparkline({ data, stroke }) {
  const w = 56;
  const h = 22;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function RadarPreview() {
  const { lang } = useLang();
  const m = urunMetinleri(lang);
  const [collapsed, setCollapsed] = useState(false);
  const [typed, setTyped] = useState('');
  const [resIdx, setResIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const idxRef = useRef(0);
  const charRef = useRef(0);
  const delRef = useRef(false);
  const timerRef = useRef(null);
  const pausedRef = useRef(false);
  const scheduleRef = useRef(null);

  useEffect(() => {
    const schedule = (ms) => {
      timerRef.current = setTimeout(run, ms);
    };
    const run = () => {
      const q = SEARCHES[idxRef.current].query;
      if (!delRef.current) {
        charRef.current += 1;
        setTyped(q.slice(0, charRef.current));
        if (charRef.current >= q.length) {
          setResIdx(idxRef.current);
          delRef.current = true;
          schedule(4200); // sonuçlar görünürken 1-2 sn daha uzun bekle
          return;
        }
        schedule(90 + Math.random() * 85);
      } else {
        charRef.current -= 1;
        setTyped(q.slice(0, Math.max(0, charRef.current)));
        if (charRef.current <= 0) {
          charRef.current = 0;
          delRef.current = false;
          idxRef.current = (idxRef.current + 1) % SEARCHES.length;
          schedule(750);
          return;
        }
        schedule(45 + Math.random() * 35);
      }
    };
    scheduleRef.current = schedule;
    schedule(700);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const hoveredRef = useRef(false);

  // Duraklat = fare panel üzerinde (mobil/tablet: parmak panelde). Kaynaktaki
  // ikinci tetikleyici (modal açık) burada yok, çünkü modal taşınmadı.
  const sync = useCallback(() => {
    const shouldPause = hoveredRef.current;
    if (shouldPause) {
      if (!pausedRef.current) {
        pausedRef.current = true;
        setPaused(true);
        if (timerRef.current) clearTimeout(timerRef.current);
      }
    } else if (pausedRef.current) {
      pausedRef.current = false;
      setPaused(false);
      if (scheduleRef.current) scheduleRef.current(160);
    }
  }, []);

  const onEnter = () => {
    hoveredRef.current = true;
    sync();
  };
  const onLeave = () => {
    hoveredRef.current = false;
    sync();
  };

  const set = SEARCHES[resIdx];

  return (
    <div
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchStart={onEnter}
      onTouchEnd={onLeave}
      onTouchCancel={onLeave}
    >
      <div aria-hidden className="pointer-events-none absolute -inset-6 rounded-[2rem] wh-radar-brand-gradient opacity-[0.08] blur-2xl" />
      <div className={`relative overflow-hidden rounded-2xl border border-border bg-card/80 shadow-2xl backdrop-blur-xl ${paused ? 'wh-radar-paused' : ''}`}>
        {/* Pencere başlığı + canlı arama + tıklanabilir kontroller */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <div className="flex gap-1.5">
            <button type="button" aria-label={m.radar.kapat} title={m.radar.kapat} onClick={() => setCollapsed(true)} className="group h-3 w-3 rounded-full bg-red-400/80 transition-transform hover:scale-110">
              <X className="mx-auto h-2 w-2 text-red-900/0 group-hover:text-red-900/80" />
            </button>
            <button type="button" aria-label={m.radar.kucult} title={m.radar.kucult} onClick={() => setCollapsed(true)} className="h-3 w-3 rounded-full bg-amber-400/80 transition-transform hover:scale-110" />
            <button type="button" aria-label={m.radar.yenidenAc} title={m.radar.yenidenAc} onClick={() => setCollapsed(false)} className="h-3 w-3 rounded-full bg-emerald-400/80 transition-transform hover:scale-110" />
          </div>
          <div className="ml-1 flex flex-1 items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs text-muted-foreground">
            <Search className="h-3.5 w-3.5 wh-radar-violet" />
            <span className="truncate">{typed}</span>
            <span className="wh-radar-anim ml-0.5 inline-block h-3.5 w-px animate-pulse wh-radar-caret" />
          </div>
          <span className="rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground">US</span>
        </div>

        {collapsed ? (
          <button type="button" onClick={() => setCollapsed(false)} className="flex w-full items-center justify-center gap-2 px-4 py-6 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
            <RotateCcw className="h-3.5 w-3.5" />
            {m.radar.kapali}
          </button>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="wh-radar-brand-text">{m.radar.aiSonuclari}</span>
              <span>{set.count} {m.radar.sonuc}</span>
            </div>
            {/* Ürün satırları — tek tek yüklenir (staggered), arama kelimesine göre değişir */}
            <div className="divide-y divide-border">
              {set.products.map((p, i) => {
                const tone = TONES[p.tone];
                return (
                  <div
                    key={`${resIdx}-${p.id}`}
                    style={{ '--i': i }}
                    className="wh-radar-anim wh-radar-row-in flex w-full items-center gap-3 px-4 py-3 text-left"
                  >
                    <ProductThumb src={p.img} alt={m.urunler[p.id]} className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-border" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{m.urunler[p.id]}</div>
                      <div className="truncate text-xs text-muted-foreground">{p.brand} · BSR {p.bsr}</div>
                    </div>
                    <Sparkline data={p.spark} stroke={tone.stroke} />
                    <div className="w-16 text-right text-sm font-semibold tabular-nums">{p.price}</div>
                    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone.chip}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                      {p.verdict}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              <span>{m.radar.satirSec}</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full wh-radar-live-dot" />
                {m.radar.canliVeri}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
